import type { ObservedPrice } from '../core/pricing'
import type { Coin } from '../core/types'
import type { CapacityEnvelope } from '../core/capacityEnvelope'
import type { BidDto, BidIdentity, BidsDto, CapacityEnvelopeDto, CapacityVectorDto, CatalogHostDto, DomainLease, DomainLeaseList, DomainOfferList, DomainProvider, LeaseDto, LeaseIdDto, LeaseListDto, PendingDeployment, ProviderDto } from './types'

const GUIDED_FLOOR = { cpuMilli: 250, memoryBytes: 1024 ** 3, storageBytes: 1024 ** 3, gpuUnits: 0 }

function capacityAxis(value: unknown, fallback: number | null = null): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback
}

function toCapacityVector(value: CapacityVectorDto | null | undefined, fallback: CapacityVectorDto | null = null): CapacityVectorDto {
  return {
    cpuMilli: capacityAxis(value?.cpuMilli, fallback?.cpuMilli ?? null),
    memoryBytes: capacityAxis(value?.memoryBytes, fallback?.memoryBytes ?? null),
    storageBytes: capacityAxis(value?.storageBytes, fallback?.storageBytes ?? null),
    gpuUnits: capacityAxis(value?.gpuUnits, fallback?.gpuUnits ?? null),
  }
}

/** Turn the envelope wire shape into a total, feature-safe domain model. */
export function toCapacityEnvelope(dto: CapacityEnvelopeDto): CapacityEnvelope {
  const dataAvailable = dto.data_available === true
  const hosts = dataAvailable ? (Array.isArray(dto.hosts) ? dto.hosts.map((host) => toCapacityVector(host)) : []) : []
  // An older console sends no groups; each row is then its own provider, which
  // is what every row was before node resolution existed.
  const groups = Array.isArray(dto.host_groups) ? dto.host_groups : []
  const hostGroups = hosts.map((_, index) => {
    const group = groups[index]
    return typeof group === 'number' && Number.isInteger(group) ? `g:${group}` : `row:${index}`
  })
  return {
    resolution: dto.resolution === 'node' ? 'node' : 'provider',
    dataAvailable,
    observedHosts: hosts.length,
    staleHosts: Math.max(0, capacityAxis(dto.stale_hosts, 0) ?? 0),
    max: toCapacityVector(dto.max),
    floor: toCapacityVector(dto.floor, GUIDED_FLOOR),
    hosts,
    hostGroups,
  }
}
export function toProvider(dto: ProviderDto): DomainProvider {
  return {
    address: dto.owner,
    name: dto.organization || dto.owner,
    region: dto.region || 'Unknown region',
    tier: dto.tier || 'Unknown tier',
    hostUri: safeUrl(dto.host_uri),
    capabilities: dto.attributes ?? {},
    audited: dto.audited,
    audits: dto.audits ?? [],
  }
}

/** The chain's lease key, reading `lease.id` and accepting the legacy `lease.lease_id`. */
export function leaseIdOf(dto: LeaseDto | null | undefined): LeaseIdDto | undefined {
  return dto?.lease?.id ?? dto?.lease?.lease_id
}

/**
 * The lease for one deployment, or none.
 *
 * Deliberately no "first lease" fallback: the list is every lease the account
 * holds, so falling back to its head would show another instance's host on
 * this one.
 */
export function leaseForDseq<T extends LeaseDto>(leases: readonly T[] | undefined, dseq: string): T | undefined {
  return leases?.find((item) => leaseIdOf(item)?.dseq === dseq)
}

export function toLease(dto: LeaseDto, providers: Map<string, DomainProvider>): DomainLease {
  const id = leaseIdOf(dto)
  const providerAddress = id?.provider ?? ''
  const heights = leaseHeights(dto)
  return {
    id: [id?.dseq, id?.gseq, id?.oseq].filter((part) => part !== undefined).join('/'),
    dseq: id?.dseq ?? 'unknown',
    provider: providers.get(providerAddress) ?? {
      address: providerAddress,
      // Keep chain addresses out of the primary tenant surface; the technical
      // provider reference belongs in a collapsed record on the detail route.
      name: providerAddress ? 'Assigned host' : 'Unknown host',
      region: 'Unknown region',
      tier: 'Unknown tier',
      hostUri: '',
      capabilities: {},
      audited: null,
      audits: [],
    },
    state: normalizeLeaseState(dto.lease?.state),
    rate: dto.lease?.price ?? { denom: 'uddt', amount: '0' },
    createdHeight: heights.created,
    closedHeight: heights.closed,
  }
}

function blockHeight(value: unknown): number | null {
  const text = typeof value === 'number' ? String(value) : value
  if (typeof text !== 'string' || !/^\d+$/.test(text)) return null
  const height = Number(text)
  // Zero is the chain's "not set" (an open lease has `closed_on: "0"`).
  return Number.isSafeInteger(height) && height > 0 ? height : null
}

/**
 * The lease's creation and close heights, as the chain reports them.
 *
 * market v1beta5 lease JSON: `lease.created_at` and `lease.closed_on`, int64
 * block heights serialised as strings. Anything else is null, never guessed.
 */
export function leaseHeights(dto: LeaseDto | null | undefined): { created: number | null; closed: number | null } {
  return { created: blockHeight(dto?.lease?.created_at), closed: blockHeight(dto?.lease?.closed_on) }
}

export function toLeaseList(dto: LeaseListDto): DomainLeaseList {
  return { owner: dto.owner, leases: dto.leases.map((lease) => toLease(lease, new Map())), count: dto.count }
}

export function normalizeLeaseState(state: string | undefined): DomainLease['state'] {
  switch (state) {
    case 'active':
      return 'active'
    case 'insufficient_funds':
      return 'insufficient-funds'
    case 'closed':
      return 'closed'
    default:
      return 'unknown'
  }
}

export function money(value: Coin | null | undefined): Coin | null {
  return value && value.denom && value.amount ? value : null
}

export function safeUrl(value: string): string {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : ''
  } catch {
    return ''
  }
}


/**
 * The chain's bid key, reading `bid.id` and accepting the legacy `bid.bid_id`.
 *
 * Returns `null` only when the offer carries no provider — an offer that names
 * no provider cannot be turned into a lease, and that is the single condition
 * every caller has to handle. `gseq`/`oseq` default to 1, the chain's own
 * first sequence, rather than to 0: a missing sequence means "the first group
 * and order of this deployment", and 0 addresses no order at all.
 */
export function bidIdOf(dto: BidDto | null | undefined): BidIdentity | null {
  const id = dto?.bid?.id ?? dto?.bid?.bid_id
  const provider = typeof id?.provider === 'string' ? id.provider.trim() : ''
  if (!provider) return null
  return {
    provider,
    dseq: typeof id?.dseq === 'string' ? id.dseq : '',
    gseq: typeof id?.gseq === 'number' ? id.gseq : 1,
    oseq: typeof id?.oseq === 'number' ? id.oseq : 1,
  }
}


function readDeploymentDseq(item: unknown): { dseq: string; state: string } | null {
  if (!item || typeof item !== 'object') return null
  const deployment = (item as { deployment?: unknown }).deployment
  const record = (deployment && typeof deployment === 'object' ? deployment : item) as Record<string, unknown>
  const id = (record.deployment_id ?? record.id) as { dseq?: unknown } | undefined
  const dseq = id && typeof id === 'object' ? id.dseq : undefined
  const text = typeof dseq === 'string' ? dseq : typeof dseq === 'number' ? String(dseq) : ''
  if (!text) return null
  return { dseq: text, state: typeof record.state === 'string' && record.state ? record.state : 'unknown' }
}

/**
 * Open deployments with no lease against them, newest chain sequence first.
 *
 * A closed deployment is excluded; an unreadable one is dropped rather than
 * shown with an invented sequence, because the dseq is the link target.
 */
export function pendingDeployments(items: readonly unknown[] | undefined, leasedDseqs: Iterable<string>): PendingDeployment[] {
  const leased = new Set(leasedDseqs)
  const seen = new Set<string>()
  const pending: PendingDeployment[] = []
  for (const item of items ?? []) {
    const record = readDeploymentDseq(item)
    if (!record || record.state === 'closed' || leased.has(record.dseq) || seen.has(record.dseq)) continue
    seen.add(record.dseq)
    pending.push(record)
  }
  return pending.sort((left, right) => (BigInt(/^\d+$/.test(right.dseq) ? right.dseq : '0') > BigInt(/^\d+$/.test(left.dseq) ? left.dseq : '0') ? 1 : -1))
}

/** The indicative prices the catalog currently publishes, for `lowestObservedMicrosPerHour`. */
export function observedReferencePrices(hosts: readonly CatalogHostDto[] | undefined): ObservedPrice[] {
  return (hosts ?? []).flatMap((host) =>
    host.reference_price
      ? [{ denom: host.reference_price.denom, amountPerBlock: host.reference_price.amount_per_block, blockSeconds: host.reference_price.block_seconds }]
      : [],
  )
}

/**
 * The offers screen's domain model.
 *
 * Built here rather than in the component so the bid key is resolved exactly
 * once, at the transport seam, and a screen can never render an offer whose
 * identity it has not already decided about.
 */
export function toOfferList(dto: BidsDto): DomainOfferList {
  return {
    owner: dto.owner,
    providerDataAvailable: Boolean(dto.provider_data_available),
    // Only offers that can still be taken. The chain keeps every bid on the
    // order after a lease is created — the winner as `active`, the rest as
    // `lost` — and listing those let a tenant "choose" an offer already won,
    // which the chain refuses with "bid not open". A bid with no state is the
    // older response shape and is kept.
    offers: (dto.bids ?? []).filter((item) => !item.bid?.state || item.bid.state === 'open').map((item) => ({
      id: bidIdOf(item),
      price: money(item.bid?.price),
      region: item.provider_info?.region ?? '',
      tier: item.provider_info?.tier ?? '',
    })),
  }
}
